import { useTranslation } from "react-i18next";
import * as yup from "yup";

function useTicketValidation() {
  const { t } = useTranslation("validationsSchema");

  const optionValueSchema = yup.object({
    id: yup.string().nullable().notRequired(),
    label: yup.string().required(t("fieldMustExist", { fieldName: "option value label" })),
    priceDelta: yup
      .number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .typeError(t("mustBeNumber"))
      .required(t("fieldMustExist", { fieldName: "option value price" }))
      .min(0, t("priceCannotBeNegative")),
    stock: yup
      .mixed()
      .transform((value, originalValue) => {
        if (originalValue === "" || originalValue === null || originalValue === undefined) return null;
        return value;
      })
      .nullable()
      .test("stock-number", t("mustBeNumber"), (value) => {
        if (value === null || value === undefined) return true;
        return Number.isFinite(Number(value)) && Number(value) >= 0;
      }),
    amountSold: yup
      .number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .min(0, t("numAvailableMinOne"))
      .default(0),
  });

  const optionGroupSchema = yup.object({
    id: yup.string().nullable().notRequired(),
    name: yup.string().required(t("fieldMustExist", { fieldName: "option group name" })),
    required: yup.boolean().default(false),
    selectionType: yup
      .string()
      .oneOf(["single", "multi"])
      .required(t("fieldMustExist", { fieldName: "selection type" })),
    maxSelect: yup
      .mixed()
      .transform((value, originalValue) => {
        if (originalValue === "" || originalValue === null || originalValue === undefined) return null;
        return value;
      })
      .nullable()
      .test("max-select", t("mustBeNumber"), (value) => {
        if (value === null || value === undefined) return true;
        return Number.isFinite(Number(value)) && Number(value) >= 1;
      }),
    values: yup
      .array()
      .of(optionValueSchema)
      .min(1, t("fieldMustExist", { fieldName: "option values" })),
  });

  let schema = yup.object({
    name: yup.string().required(t("ticketNameRequired")).min(3, t("ticketNameTooShort")),

    maxUnitPerSale: yup
      .number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .typeError(t("mustBeNumber"))
      .required(t("maxUnitsRequired"))
      .min(1, t("maxUnitsMinOne")),

    price: yup
      .number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .typeError(t("mustBeNumber"))
      .required(t("priceRequired"))
      .min(0, t("priceCannotBeNegative")),

    numAvailable: yup
      .number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .typeError(t("mustBeNumber"))
      .required(t("numAvailableRequired"))
      .min(0, t("numAvailableMinOne")),

    description: yup
      .string()
      .nullable()
      .transform((value, originalValue) => {
        if (typeof originalValue !== "string") return null;
        return originalValue.trim() === "" ? null : originalValue;
      }),

    secret: yup.boolean(),

    image: yup.string().nullable().notRequired(),

    availableFrom: yup
      .mixed()
      .nullable()
      .transform((value) => {
        if (value === "" || value === null || value === undefined) return null;
        if (typeof value === "number") return new Date(value);
        if (value instanceof Date) return value;
        return new Date(value);
      }),

    availableTo: yup
      .mixed()
      .nullable()
      .transform((value) => {
        if (value === "" || value === null || value === undefined) return null;
        if (typeof value === "number") return new Date(value);
        if (value instanceof Date) return value;
        return new Date(value);
      })
      .test("dates-dependency", t("availableFromRequiredWhenToSet"), function (value) {
        const { availableFrom } = this.parent;
        if (value && !availableFrom) return false;
        return true;
      })
      .test("dates-order", t("availableToMustBeAfterFrom"), function (value) {
        const { availableFrom } = this.parent;
        if (value && availableFrom && new Date(value) < new Date(availableFrom)) return false;
        return true;
      }),

    options: yup.array().of(optionGroupSchema).nullable().notRequired(),
  });

  schema = schema.test(
    "availability-dates",
    t("availableToRequiredWhenFromSet"),
    function (values) {
      const { availableFrom, availableTo } = values;
      if (availableFrom && !availableTo) {
        return this.createError({
          path: "availableTo",
          message: t("availableToRequiredWhenFromSet"),
        });
      }
      return true;
    },
  );

  return schema;
}

export default useTicketValidation;
